import {Component, ComponentProperty, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';

import {mat4, quat, vec3} from 'gl-matrix';
import {Particle} from './Particle.js';
import {lerp} from '../../utils/lerp.js';
import {AxisX, AxisY, AxisZ, Mathf} from '../../utils/Mathf.js';
import {BlobPrefabs} from '../blob-prefabs.js';
import {RNG} from '../../utils/rng.js';

const GRAVITY = -9.81;

export enum RenderType {
    Normal,
    Billboard,
    StretchedBillboard,
}

class ScaleOverLifetime {
    @property.float(1)
    start = 1;
    @property.float(0)
    end = 0;
}

class RotationOverLifetime {
    @property.float(0)
    angularVelocityA = 0;
    @property.float(0)
    angularVelocityB = 0;
}

/**
 * Base component for particle systems in Wonderland Engine
 * Handles creation, lifecycle management and updating of particle objects
 */
export class SimpleParticles extends Component {
    static TypeName = 'simple-particles';

    /**
     * Object to clone for each particle in the system
     */
    @property.object({required: true})
    particlePrefab!: Object3D;

    /**
     * Maximum number of particles that can exist at once
     */
    @property.int(12)
    maxParticles!: number;

    /**
     * Scale factor applied to each particle
     */
    @property.float(1)
    particleScale!: number;

    /**
     * The time in seconds the particles will live for
     */
    @property.float(1)
    lifeTime = 1;

    /**
     * Size parameter for the particle system
     */
    @property.int(16)
    size!: number;

    /**
     * The time in seconds the particles will emit for. If set to 0, particles will burst at start.
     */
    @property.float(0.1)
    emissionRateOverTime = 0.1;

    /**
     * The time in seconds the particles will emit for. If set to 0, particles will burst at start.
     */
    @property.float(0.25)
    duration = 0.25;

    @property.record(RotationOverLifetime)
    rotationOverLifetime = new RotationOverLifetime();

    @property.vector3(0, 0, 0)
    velocityOverLifetime = [0, 0, 0];

    @property.record(ScaleOverLifetime)
    scaleOverLifetime = new ScaleOverLifetime();

    @property.enum(
        Object.keys(RenderType).filter((e) => isNaN(Number(e))),
        'StretchedBillboard'
    )
    renderType: RenderType = RenderType.StretchedBillboard;

    /**
     * Initial speed of particles when spawned
     */
    @property.float(15)
    initialSpeed!: number;

    @property.float(1)
    gravityModifier = 1;

    @property.float(25)
    angle = 25;

    @property.float(0.5)
    radius = 0.5;

    @property.float(0)
    startRotationA = 0;

    @property.float(0)
    startRotationB = 0;

    /**
     * Elapsed time since particle system started
     */
    time: number = 0.0;

    /**
     * Number of particles spawned so far
     */
    count: number = 0;

    /**
     * Collection of all particle objects
     */
    private _objects: Object3D[] = [];
    private _spawned: Particle[] = [];

    /**
     * Speed values for each particle
     */
    private _speeds: number[] = [];

    /**
     * Direction vectors for each particle
     */
    private _direction: vec3[] = [];

    /**
     * Current scale values for each particle
     */
    private _scales: number[] = [];

    /**
     * Reusable vector for position calculations to avoid garbage collection
     */
    private _tempVec3: vec3 = vec3.create();
    private _tempVec3_2: vec3 = vec3.create();
    private _tempVec3_3: vec3 = vec3.create();
    private _updateParticleRotationQuat: quat = quat.create();

    private _calcStartDirAlignmentQuat: quat = quat.create();
    private _calcStartDirDirection: vec3 = vec3.create();
    private _cameraPos: vec3 = vec3.create();
    /**
     * Initializes the particle system by creating all potential particles
     * and spawning the initial batch
     */
    start() {
        this._objects = [];
        for (let i = 0; i < this.maxParticles; i++) {
            const p = this.particlePrefab.clone(this.object);
            //p.resetPositionRotation();
            p.scaleLocal([0, 0, 0]);

            this.initParticle(p, i);

            this._objects.push(p);
        }

        // Spawn all particles at once if emission rate is 0
        if (this.emissionRateOverTime <= 0) {
            for (let i = 0; i < this.maxParticles; ++i) {
                this.spawn();
            }
        }
    }

    /**
     * Override this function to initialize the particle object.
     * This is called for every particle when initialized
     * @param particle A particle object to initialize
     * @param index The index of the particle
     */
    initParticle(particle: Object3D, index: number) {
        this._speeds[index] = this.initialSpeed;
        this._scales[index] = this.particleScale;
        this._direction[index] = this._calculateStartDirectionCone();
        particle.setPositionLocal(this._calculateStartPositionCone());
    }

    /**
     * Called when a particle is spawned
     * Override to customize behavior when a particle appears
     * @param particle The particle object that was just spawned
     * @param index The index of the spawned particle
     */
    onParticleSpawn(particle: Object3D, index: number) {}

    /**
     * Number of particles to spawn in the next frame
     */
    private _shouldSpawn = 0;

    /**
     * Updates the particle system each frame
     * Handles particle lifetime and updates individual particles
     * @param dt Delta time in seconds since last update
     */
    update(dt: number) {
        this.time += dt;

        //TK: This needs to be improved, but because particles are spawned over time and their lifetime
        // can vary, the exact lifetime is not fixed. For looping systems, they do not
        // need to be destroyed automatically.
        if (this.time > 5) {
            // this.lifeTime) {
            this.object.destroy();
            return;
        }

        // Handle particle emission based on the configured rate
        if (this.emissionRateOverTime > 0) {
            if (this.time < this.duration) {
                const toSpawn = this._updateSpawnRate(dt);
                if (toSpawn > 0) {
                    for (let i = 0; i < toSpawn && this.count < this.maxParticles; i++) {
                        this.spawn();
                    }
                }
            }
        }
        this.engine.scene.activeViews[0].object.getPositionWorld(this._cameraPos);
        for (let i = 0; i < this.count; i++) {
            if (this._spawned[i]) {
                this.updateParticle(this._spawned[i], i, dt);
            }
        }
    }

    _updateSpawnRate(dt: number) {
        this._shouldSpawn += this.emissionRateOverTime * dt;
        const toSpawn = Math.floor(this._shouldSpawn);
        this._shouldSpawn -= toSpawn; // keep the remainder
        return toSpawn;
    }

    /**
     * Spawns a new particle or reuses an existing one if at max capacity
     */
    spawn() {
        let index = this.count % this.maxParticles;

        let obj = this._objects[index];
        obj.resetTransform();
        obj.scaleLocal([this.particleScale, this.particleScale, this.particleScale]);
        this.object.getPositionWorld(this._tempVec3);
        obj.setPositionWorld(this._tempVec3);

        this.onParticleSpawn(obj, index);

        this.count += 1;
        this._spawned[index] = new Particle(
            this.lifeTime,
            this._objects[index],
            RNG.getUniform(
                this.rotationOverLifetime.angularVelocityA,
                this.rotationOverLifetime.angularVelocityB
            ),
            RNG.getUniform(this.startRotationA, this.startRotationB)
        );
    }

    private _velocity = vec3.create();
    private _dropletToCamera = vec3.create();
    private _projected = vec3.create();
    private _right = vec3.create();
    private _correctedUp = vec3.create();
    private _twistMat = mat4.create();
    private _finalMat = mat4.create();

    /**
     * Updates a particle's position, rotation, and scale each frame.
     * Applies gravity, constant velocity over lifetime, and billboard stretching
     * to make the particle face the camera and stretch along its movement direction.
     * The particle's scale is interpolated from `startScale` to `endScale` over its lifetime.
     * @param particle The particle instance to update.
     * @param index The index of the particle.
     * @param dt Delta time in seconds since the last update.
     */
    updateParticle(particle: Particle, index: number, dt: number): void {
        particle.update(dt);

        if (particle.isAlive === false) {
            return;
        }
        if (this.gravityModifier !== 0) {
            this._direction[index][1] += GRAVITY * this.gravityModifier * dt;
        }

        // Apply velocity over lifetime directly
        this._direction[index][0] += this.velocityOverLifetime[0] * dt;
        this._direction[index][1] += this.velocityOverLifetime[1] * dt;
        this._direction[index][2] += this.velocityOverLifetime[2] * dt;

        const moveVec = vec3.scale(
            this._tempVec3_2,
            this._direction[index],
            this._speeds[index] * dt
        );
        // // Get current position (in local space)
        particle.object.getPositionWorld(this._tempVec3);

        const progress = particle.lifeProgress;
        const s = lerp(this.scaleOverLifetime.start, this.scaleOverLifetime.end, progress);

        particle.currentRotation += particle.angularVelocity * dt;
        // this.rotationOverLifetime.angularVelocityA != this.rotationOverLifetime.angularVelocityB
        //     ? ((this.rotationOverLifetime.end - this.rotationOverLifetime.start) /
        //           particle.lifetime) *
        //       progress
        //     : 0;

        // // Add movement to current position
        vec3.add(this._tempVec3, this._tempVec3, moveVec);

        if (this.renderType === RenderType.StretchedBillboard) {
            vec3.subtract(this._dropletToCamera, this._cameraPos, this._tempVec3);
            vec3.normalize(this._dropletToCamera, this._dropletToCamera);

            vec3.cross(this._right, AxisY, this._dropletToCamera);
            vec3.normalize(this._right, this._right);

            vec3.cross(this._correctedUp, this._dropletToCamera, this._right);

            // Build a rotation matrix that faces the camera

            const billboardMat = mat4.fromValues(
                this._right[0],
                this._right[1],
                this._right[2],
                0,
                this._correctedUp[0],
                this._correctedUp[1],
                this._correctedUp[2],
                0,
                this._dropletToCamera[0],
                this._dropletToCamera[1],
                this._dropletToCamera[2],
                0,
                0,
                0,
                0,
                1
            );

            vec3.normalize(this._velocity, this._direction[index]);

            // Project velocity onto the plane defined by the billboard (remove forward component)
            const forward = vec3.clone(this._dropletToCamera);

            const dot = vec3.dot(this._velocity, forward);
            vec3.scale(this._tempVec3_3, forward, dot);
            vec3.subtract(this._projected, this._velocity, this._tempVec3_3);
            vec3.normalize(this._projected, this._projected);

            // Compute angle between 'right' and 'projected'
            const angle = Math.atan2(
                vec3.dot(this._correctedUp, this._projected),
                vec3.dot(this._right, this._projected)
            );

            // Apply twist (rotation around Z of billboard)

            mat4.fromZRotation(this._twistMat, angle);

            // Combine twist + billboard
            mat4.multiply(this._finalMat, billboardMat, this._twistMat);

            particle.object.setPositionWorld(this._tempVec3);
            particle.object.setRotationWorld(
                mat4.getRotation(this._updateParticleRotationQuat, this._finalMat)
            );
            particle.object.setScalingWorld([
                s * this._scales[index],
                s * this._scales[index],
                s * this._scales[index],
            ]);
        } else if (this.renderType === RenderType.Billboard) {
            particle.object.setPositionWorld(this._tempVec3);
            particle.object.lookAt(this._cameraPos, AxisY);
            particle.object.rotateAxisAngleDegObject(AxisY, 180);
            particle.object.rotateAxisAngleDegObject(AxisZ, particle.currentRotation);

            particle.object.setScalingWorld([
                s * this._scales[index],
                s * this._scales[index],
                s * this._scales[index],
            ]);
        }
    }

    /**
     * Calculates a random starting position for a particle within a circular base of a cone.
     * The position is relative to the particle system's object.
     * @returns A Float32Array representing the local [x, y, z] position.
     */
    private _calculateStartPositionCone(): Float32Array {
        const radius = this.radius;
        const angle = Math.random() * 2 * Math.PI; // Random angle in the circle
        const x = radius * Math.cos(angle);
        const y = 0; // Assuming the base of the cone is at y = 0
        const z = radius * Math.sin(angle);
        return new Float32Array([x, y, z]);
    }

    /**
     * Calculates a random initial emission direction for a particle.
     * The direction is within a cone defined by `this.angle` and aligned
     * with the forward vector of the particle system's object.
     * @returns A Float32Array representing the [x, y, z] direction vector.
     */
    private _calculateStartDirectionCone(): Float32Array {
        const emissionAngle = this.angle; // Angle in degrees
        const emissionAngleRad = (emissionAngle * Math.PI) / 180; // Convert to radians

        // Generate a random direction within the cone angle
        const theta = Math.random() * emissionAngleRad; // Random angle within the cone
        const phi = Math.random() * 2 * Math.PI; // Random angle around the circle

        const x = Math.sin(theta) * Math.cos(phi);
        const y = Math.cos(theta);
        const z = Math.sin(theta) * Math.sin(phi);

        this.object.getForwardWorld(this._tempVec3);
        // Create a rotation that aligns the Y axis with our forward direction
        const alignmentQuat = this._calcStartDirAlignmentQuat;
        quat.rotationTo(alignmentQuat, [0, 1, 0], this._tempVec3);

        // Apply the rotation to our generated direction
        const direction = this._calcStartDirDirection;
        vec3.set(direction, x, y, z);
        vec3.transformQuat(direction, direction, alignmentQuat);

        return new Float32Array([direction[0], direction[1], direction[2]]);
    }
}
